import { Stack, Text } from '@ltvco/refresh-lib/theme';
import { Record } from '@ltvco/refresh-lib/v1';

import { PersonParamsFunctionArguments } from 'components/ChatBV/types';
import { formatShorthandDate } from 'routes/SearchPerson/Cards/NewCardsDesignVariation1/common/utils';

import { PersonReportSummaryData } from '../DataTypes/SummaryTypes';

interface PersonTeaserRendererProps {
  data: Record[] | undefined;
  params: PersonParamsFunctionArguments | undefined;
}

interface PersonReportRendererProps {
  data: PersonReportSummaryData;
}

export const PersonTeaserRenderer = ({
  data,
  params,
}: PersonTeaserRendererProps) => {
  const hasLocation = params?.city || params?.state;
  const hasAge = params?.age;
  return (
    <Stack spacing={3} py={2}>
      <Text>
        We found {data?.length || 0} result(s) for individuals with the name{' '}
        <span style={{ fontWeight: 700 }}>
          {params?.first_name || ''}{' '}
          {params?.middle_name ? `${params?.middle_name} ` : ''}
          {params?.last_name || ''}
        </span>
        {hasLocation ? ' located in ' : ''}
        {hasLocation ? (
          <span style={{ fontWeight: 700 }}>
            {params?.city || ''}
            {params?.state ? `, ${params?.state}` : ''}
          </span>
        ) : (
          ''
        )}
        {hasAge ? ' aged ' : ''}
        {hasAge ? (
          <span style={{ fontWeight: 700 }}>{params?.age || ''}</span>
        ) : (
          ''
        )}
      </Text>
      <Text>
        Try adding more filters to narrow down the possible results or click on
        one of the results below to see more information about that person.
      </Text>
      <Text>Showing results in the United States only.</Text>
    </Stack>
  );
};

export const PersonReportRenderer = ({ data }: PersonReportRendererProps) => {
  const hasLocation = data?.location;
  return (
    <Stack spacing={3} py={2}>
      <Text>
        Here are the key details for{' '}
        <span style={{ fontWeight: 700 }}>{data.personName}</span>
        {hasLocation ? ' located in ' : ''}
        {hasLocation ? (
          <span style={{ fontWeight: 700 }}>{data.location}</span>
        ) : (
          ''
        )}
      </Text>
      <Stack spacing={1.5}>
        <Text>
          <span style={{ fontWeight: 700 }}>Summary</span>
        </Text>
        <Text>
          <span style={{ fontWeight: 700 }}>Age:</span> {data.age || '-'}
        </Text>
        <Text>
          <span style={{ fontWeight: 700 }}>Born:</span>{' '}
          {typeof data?.dateOfBirth === 'string'
            ? data?.dateOfBirth
            : formatShorthandDate(data?.dateOfBirth) || '-'}
        </Text>
        <Text>
          <span style={{ fontWeight: 700 }}>Aliases:</span>{' '}
          {data.aliases?.join(', ') || '-'}
        </Text>
      </Stack>
      <Stack spacing={1.5}>
        <Text>
          <span style={{ fontWeight: 700 }}>Contact Info</span>
        </Text>
        <Text>📞 {data.phone || '-'}</Text>
        <Text>✉️ {data.email || '-'}</Text>
        <Text>🏠 {data.address || '-'}</Text>
      </Stack>
      <Stack spacing={1.5}>
        <Text>
          <span style={{ fontWeight: 700 }}>Work & Education</span>
        </Text>
        {data.jobs && <Text>{data.jobs}</Text>}
        {data.education && <Text>{data.education}</Text>}
        {!data.jobs && !data.education && <Text>-</Text>}
      </Stack>
      <Stack spacing={1.5}>
        <Text>
          <span style={{ fontWeight: 700 }}>Relatives</span>
        </Text>
        {data.relatives?.length > 0 ? (
          data.relatives.map((relative) => (
            <Text key={relative}>• {relative}</Text>
          ))
        ) : (
          <Text>-</Text>
        )}
      </Stack>
    </Stack>
  );
};
